/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CloseIcon from "@mui/icons-material/Close";
import gql from "graphql-tag";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getCoupon, getTipping } from "../../apollo/server";
import ConfigurationContext from "../../context/Configuration";
import UserContext from "../../context/User";
import CartItem from "../RestaurantDetailComponent/RestaurantCart/CartItem";
import Voucher from "./Voucher";
import { useTranslation } from "react-i18next";

const TIPPING = gql`
  ${getTipping}
`;
const GET_COUPON = gql`
  ${getCoupon}
`;

// Same explanations the app shows behind each fee's info icon
const FEE_INFO = {
  delivery:
    "This is the cost of delivery to you. It can vary such as your location to the selected store and, sometimes the time of day.",
  smallOrder:
    "You can order less for a small fee. Add more items to your order to remove this fee",
  bag: "Businesses are legally required to charge customers for certain types of carrier bags. The charge is calculated based on the average number of bags used per order.",
  service:
    "This allows us to provide and keep improving our service (including customer support, assistance with packaging when you are buying groceries) and improving your overall experience",
  tip: "100% of your tip goes to your courier. Tipping is a great way to thank them for their service.",
};

const NOTE_MAX = 200;
const money = (value) => `£${(Number(value) || 0).toFixed(2)}`;

function Section({ title, action, children }) {
  return (
    <Box mt={3}>
      {(title || action) && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography style={{ fontWeight: 700, fontSize: "1rem", color: "#111" }}>
            {title}
          </Typography>
          {action}
        </Box>
      )}
      <Paper
        elevation={0}
        style={{
          border: "1px solid #ECECEC",
          borderRadius: 16,
          padding: "12px 16px",
          background: "#fff",
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

function InfoTip({ text }) {
  return (
    <Tooltip title={text} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
      <InfoOutlinedIcon style={{ fontSize: 16, color: "#8A8A8A", marginLeft: 6, cursor: "pointer" }} />
    </Tooltip>
  );
}

function BillRow({ label, info, value, valueColor, muted, bold }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
      <Box display="flex" alignItems="center" style={{ minWidth: 0 }}>
        <Typography
          style={{
            fontSize: muted ? "0.8rem" : "0.9rem",
            fontWeight: bold ? 800 : muted ? 400 : 600,
            color: muted ? "#6B6B6B" : "#111",
            paddingLeft: muted ? 12 : 0,
          }}
        >
          {label}
        </Typography>
        {info && <InfoTip text={info} />}
      </Box>
      <Box display="flex" alignItems="center">
        {typeof value === "string" ? (
          <Typography
            style={{
              fontSize: muted ? "0.8rem" : bold ? "1.05rem" : "0.9rem",
              fontWeight: bold ? 800 : muted ? 400 : 600,
              color: valueColor || (muted ? "#6B6B6B" : "#111"),
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}

function ActionRow({ icon, label, detail, onClick }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      py={1.25}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      {icon}
      <Box ml={1.5} flex={1} style={{ minWidth: 0 }}>
        <Typography style={{ fontWeight: 700, fontSize: "0.92rem", color: "#111" }}>
          {label}
        </Typography>
        {detail && (
          <Typography style={{ fontSize: "0.78rem", color: "#6B6B6B" }}>{detail}</Typography>
        )}
      </Box>
      <ChevronRightIcon style={{ color: "#9A9A9A" }} />
    </Box>
  );
}

// Collapsible note with a draft, like the app's ExpandableNote: nothing is
// kept until Save, and Cancel restores what was saved before
function NoteBox({ icon, title, subtitle, value, onSave }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);

  return (
    <Box py={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setOpen((prev) => !prev)}
        style={{ cursor: "pointer" }}
      >
        <Box display="flex" alignItems="center">
          {icon}
          <Typography style={{ fontWeight: 700, fontSize: "0.92rem", color: "#111", marginLeft: 12 }}>
            {title}
          </Typography>
        </Box>
        <IconButton size="small">
          {open ? (
            <RemoveCircleOutlineIcon color="primary" />
          ) : (
            <AddCircleOutlineIcon color="primary" />
          )}
        </IconButton>
      </Box>
      {!open && value ? (
        <Typography style={{ fontSize: "0.8rem", color: "#555", marginTop: 4, wordBreak: "break-word" }}>
          {value}
        </Typography>
      ) : null}
      {open && (
        <Box mt={1}>
          <Typography style={{ fontSize: "0.78rem", color: "#6B6B6B", marginBottom: 8 }}>
            {subtitle}
          </Typography>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, NOTE_MAX))}
            fullWidth
            multiline
            minRows={2}
            size="small"
            inputProps={{ maxLength: NOTE_MAX, style: { color: "#111", fontSize: "0.85rem" } }}
            helperText={`${draft.length}/${NOTE_MAX}`}
            FormHelperTextProps={{ style: { textAlign: "right", marginRight: 0 } }}
          />
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button
              size="small"
              onClick={() => {
                setDraft(value || "");
                setOpen(false);
              }}
              style={{ marginRight: theme.spacing(1), color: "#111" }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => {
                onSave(draft.trim());
                setOpen(false);
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// One dialog for both credit pots; the rules live in Checkout and come back
// here as an error string (or null once applied)
function CreditDialog({
  open,
  onClose,
  icon,
  title,
  balanceLabel,
  balance,
  hint,
  max,
  applied,
  onApply,
  onClear,
  extra,
}) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setInput("");
      setError("");
    }
  }, [open]);

  const submit = () => {
    const message = onApply(parseFloat(input));
    if (message) setError(message);
    else onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 20 } }}>
      <Box p={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            {icon}
            <Typography style={{ fontWeight: 800, fontSize: "1.1rem", marginLeft: 10, color: "#111" }}>
              {title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box mt={2} p={2} style={{ background: "#F7F7F7", borderRadius: 12 }}>
          <Typography style={{ fontSize: "0.8rem", color: "#6B6B6B" }}>{balanceLabel}</Typography>
          <Typography style={{ fontSize: "1.5rem", fontWeight: 800, color: "#111" }}>
            {money(balance)}
          </Typography>
          <Typography style={{ fontSize: "0.78rem", color: "#6B6B6B", marginTop: 4 }}>{hint}</Typography>
        </Box>

        {applied > 0 && (
          <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
            <Typography style={{ fontSize: "0.9rem", color: "#111" }}>
              <b>{money(applied)}</b> applied to this order
            </Typography>
            <Button
              size="small"
              onClick={() => {
                onClear();
                onClose();
              }}
            >
              Remove
            </Button>
          </Box>
        )}

        <Box mt={2} display="flex" gap={1}>
          <TextField
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError("");
            }}
            placeholder={`Up to ${money(max)}`}
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0, step: "0.01", style: { color: "#111" } }}
            error={Boolean(error)}
            helperText={error || " "}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={max <= 0}
            onClick={() => setInput(max.toFixed(2))}
            style={{ height: 40, whiteSpace: "nowrap" }}
          >
            Max
          </Button>
        </Box>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={max <= 0}
          onClick={submit}
          style={{ borderRadius: 10, height: 44, fontWeight: 700 }}
        >
          Apply
        </Button>
        {extra}
      </Box>
    </Dialog>
  );
}

function CartItemsCard({
  restaurantData,
  setFlashMessage,
  setTaxValue,
  coupon,
  setCoupon,
  selectedTip,
  setSelectedTip,
  customTip,
  setCustomTip,
  isPickUp,
  bill,
  freeDeliveryShortfall,
  farDistanceNotice,
  wallet,
  deliveryNotes,
  orderNotes,
  setDeliveryNotes,
  setOrderNotes,
  addQuantity,
  removeQuantity,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { cart } = useContext(UserContext);
  const configuration = useContext(ConfigurationContext);
  const currencySymbol = configuration?.currencySymbol || "£";
  const [couponText, setCouponText] = useState("");
  const [voucherModal, setVoucherModal] = useState(false);
  const [walletModal, setWalletModal] = useState(false);
  const [referralModal, setReferralModal] = useState(false);
  const [tipModal, setTipModal] = useState(false);
  const [tipInput, setTipInput] = useState("");
  const [tipError, setTipError] = useState("");
  const [referralPointsError, setReferralPointsError] = useState("");

  const { data: dataTip } = useQuery(TIPPING, { fetchPolicy: "network-only" });
  const [mutate, { loading: couponLoading }] = useMutation(GET_COUPON, {
    onCompleted: couponCompleted,
    onError: couponOnError,
  });

  useEffect(() => {
    setTaxValue(restaurantData ? +restaurantData.tax : 0);
  }, [restaurantData]);

  const mutateVoucher = (e) => {
    e.preventDefault();
    mutate({ variables: { coupon: couponText.trim() } });
  };

  function couponCompleted({ coupon: result }) {
    if (result?.enabled) {
      setCoupon(result);
      setCouponText("");
      setFlashMessage({ type: "success", message: "Coupon applied." });
      setVoucherModal(false);
    } else {
      setFlashMessage({ type: "warning", message: "Coupon unavailable." });
    }
  }
  function couponOnError(error) {
    setFlashMessage({
      type: "error",
      message: error?.graphQLErrors?.[0]?.message || error?.message || "Invalid Coupon.",
    });
  }
  const toggleVoucherModal = useCallback(() => setVoucherModal((prev) => !prev), []);

  const applyCustomTip = () => {
    const amount = Number(tipInput);
    if (!tipInput || Number.isNaN(amount)) return setTipError("Invalid amount");
    if (amount <= 0) return setTipError("Amount must be greater than 0");
    setCustomTip(amount);
    setSelectedTip(null);
    setTipInput("");
    setTipError("");
    setTipModal(false);
  };

  const couponLabel = !coupon
    ? ""
    : coupon.appliesTo === "DELIVERY"
    ? Number(coupon.discount) >= 100
      ? "Free delivery"
      : `${coupon.discount}% off delivery`
    : String(coupon.discountType || "percent").toLowerCase() === "fixed"
    ? `£${coupon.discount} discount`
    : `${coupon.discount}% discount`;

  const deliveryDiscounted =
    (bill.freeDeliveryApplied || bill.deliveryCharges < bill.originalDeliveryCharges) &&
    bill.originalDeliveryCharges > 0;
  const isGrocery = restaurantData?.shopType === "grocery";
  const tipVariations = dataTip?.tips?.tipVariations || [];

  return (
    <>
      {/* Items */}
      <Paper
        elevation={0}
        style={{ border: "1px solid #ECECEC", borderRadius: 16, padding: "0 16px", background: "#fff" }}
      >
        {cart?.map((foodItem, index) => (
          <React.Fragment key={`ITEM_${foodItem.key || index}`}>
            <CartItem
              quantity={foodItem.quantity}
              dealName={foodItem.title}
              foodTitle={foodItem.foodTitle}
              variationTitle={foodItem.variationTitle}
              optionTitles={foodItem.optionTitles}
              dealPrice={(parseFloat(foodItem.price) * foodItem.quantity).toFixed(2)}
              addQuantity={() => addQuantity(foodItem.key)}
              removeQuantity={() => {
                if (foodItem.quantity > 1) removeQuantity(foodItem.key);
              }}
            />
            {index < cart.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Paper>

      {/* Credits */}
      <Section>
        <ActionRow
          icon={<AccountBalanceWalletOutlinedIcon color="primary" />}
          label={t("applyWalletBalance", { defaultValue: "Apply Wallet Balance" })}
          detail={
            bill.walletUsed > 0
              ? `${money(bill.walletUsed)} applied · covers the service fee only`
              : `Balance ${money(wallet.walletBalance)} · covers the service fee only`
          }
          onClick={() => setWalletModal(true)}
        />
        <Divider />
        {/* Always shown: hiding it when the balance is empty left customers
            with no visible way to spend referral credit */}
        <ActionRow
          icon={<CardGiftcardIcon color="primary" />}
          label="Apply Referral Credit"
          detail={
            bill.referralUsed > 0
              ? `${money(bill.referralUsed)} applied`
              : wallet.referralBalance > 0
              ? `Balance ${money(wallet.referralBalance)} · covers any part of the bill`
              : wallet.referralPoints > 0
              ? `${wallet.referralPoints} referral points ready to use`
              : "Invite friends to earn referral credit"
          }
          onClick={() => setReferralModal(true)}
        />
      </Section>

      {/* Notes */}
      <Section title="Want to tell us something?">
        {!isPickUp && (
          <>
            <NoteBox
              icon={<NoteAltOutlinedIcon color="primary" />}
              title={t("addDeliveryNotes", { defaultValue: "Add Delivery Notes" })}
              subtitle="Add a note for the courier. Do not include allergy details, as the seller cannot see this message."
              value={deliveryNotes}
              onSave={setDeliveryNotes}
            />
            <Divider />
          </>
        )}
        <NoteBox
          icon={<ChatBubbleOutlineIcon color="primary" />}
          title={t("addOrderNotes", { defaultValue: "Add Order Notes" })}
          subtitle="Leave a message for the seller. For food allergies, contact the place directly and do not include allergy details here."
          value={orderNotes}
          onSave={setOrderNotes}
        />
      </Section>

      {/* Vouchers */}
      <Section title="Vouchers & Discounts">
        {!coupon ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            py={1}
            onClick={toggleVoucherModal}
            style={{ cursor: "pointer" }}
          >
            <Box display="flex" alignItems="center">
              <LocalOfferOutlinedIcon color="primary" />
              <Typography style={{ marginLeft: 12, fontSize: "0.92rem", color: "#111" }}>
                Add voucher discount
              </Typography>
            </Box>
            <AddCircleOutlineIcon style={{ color: "#8A8A8A" }} />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
            <Box display="flex" alignItems="center" style={{ minWidth: 0 }}>
              <LocalOfferOutlinedIcon color="primary" />
              <Box ml={1.5} style={{ minWidth: 0 }}>
                <Typography noWrap style={{ fontWeight: 700, fontSize: "0.92rem", color: "#111" }}>
                  {coupon.title} applied
                </Typography>
                <Typography style={{ fontSize: "0.78rem", color: "#6B6B6B" }}>{couponLabel}</Typography>
              </Box>
            </Box>
            <Button size="small" onClick={() => setCoupon(null)}>
              Remove
            </Button>
          </Box>
        )}
      </Section>
      {!isPickUp && freeDeliveryShortfall > 0 && (
        <Box
          mt={1.5}
          p={1.5}
          display="flex"
          alignItems="center"
          style={{ borderRadius: 12, backgroundColor: `${theme.palette.primary.main}1F` }}
        >
          <LocalShippingOutlinedIcon color="primary" style={{ marginRight: 10 }} />
          <Typography style={{ fontWeight: 700, fontSize: "0.85rem", color: "#111" }}>
            {t("freeDeliveryShortfall", {
              currencySymbol,
              amount: Number(freeDeliveryShortfall).toFixed(2),
            })}
          </Typography>
        </Box>
      )}

      {/* Tip */}
      <Section
        title={t("tipYourCourier", { defaultValue: "Add rider tip" })}
        action={<InfoTip text={FEE_INFO.tip} />}
      >
        <Typography style={{ fontSize: "0.8rem", color: "#6B6B6B", marginBottom: 12 }}>
          It's optional but a tip can brighten your courier's day
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} pb={1}>
          {tipVariations.map((tipOption, index) => {
            const active = !customTip && selectedTip === tipOption;
            return (
              <Button
                key={`TIP_${index}`}
                variant={active ? "contained" : "outlined"}
                disableElevation
                onClick={() => {
                  setCustomTip(null);
                  setSelectedTip(active ? null : tipOption);
                }}
                style={{ borderRadius: 10, minWidth: 64, fontWeight: 700 }}
              >
                £{tipOption.tip || tipOption}
              </Button>
            );
          })}
          <Button
            variant={customTip ? "contained" : "outlined"}
            disableElevation
            onClick={() => setTipModal(true)}
            style={{ borderRadius: 10, minWidth: 64, fontWeight: 700 }}
          >
            {customTip ? money(customTip) : t("other", { defaultValue: "Other" })}
          </Button>
        </Box>
      </Section>

      {/* Payment summary */}
      <Section title={t("paymentSummary", { defaultValue: "Payment Summary" })}>
        {bill.smallOrderFee > 0 && !isPickUp && (
          <BillRow
            label={t("smallOrderFee", { defaultValue: "Small Order Fee" })}
            info={FEE_INFO.smallOrder}
            value={money(bill.smallOrderFee)}
          />
        )}
        {bill.bagFee > 0 && (
          <BillRow label={t("bagFee", { defaultValue: "Bag Fee" })} info={FEE_INFO.bag} value={money(bill.bagFee)} />
        )}
        <BillRow label={t("subTotal")} value={money(bill.subtotal)} />
        {!isPickUp && (
          <BillRow
            label={t("deliveryFee")}
            info={FEE_INFO.delivery}
            value={
              deliveryDiscounted ? (
                <Box display="flex" alignItems="center">
                  <LocalOfferOutlinedIcon style={{ fontSize: 14, color: "#DC2626", marginRight: 4 }} />
                  <Typography
                    style={{ fontSize: "0.8rem", color: "#8A8A8A", textDecoration: "line-through", marginRight: 6 }}
                  >
                    {money(bill.originalDeliveryCharges)}
                  </Typography>
                  <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#DC2626" }}>
                    {bill.freeDeliveryApplied ? "Free" : money(bill.deliveryCharges)}
                  </Typography>
                </Box>
              ) : (
                money(bill.deliveryCharges)
              )
            }
          />
        )}
        <BillRow
          label={
            isGrocery
              ? t("service_fee_bag", { defaultValue: "Service Fee + Packaging" })
              : t("taxFee")
          }
          info={FEE_INFO.service}
          value={money(bill.serviceFee)}
        />
        {bill.tip > 0 && (
          <BillRow
            label={t("tip")}
            value={
              <Box display="flex" alignItems="center">
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedTip(null);
                    setCustomTip(null);
                  }}
                  style={{ minWidth: 0, marginRight: 8, fontSize: "0.72rem" }}
                >
                  {t("remove")}
                </Button>
                <Typography style={{ fontSize: "0.9rem", fontWeight: 600, color: "#111" }}>
                  {money(bill.tip)}
                </Typography>
              </Box>
            }
          />
        )}
        {coupon && coupon.appliesTo !== "DELIVERY" && (
          <BillRow
            label={`${t("voucherDiscount", { defaultValue: "Voucher Discount" })} (${
              String(coupon.discountType || "percent").toLowerCase() === "fixed"
                ? `£${coupon.discount}`
                : `${coupon.discount}%`
            })`}
            value={`-${money(bill.couponDiscount)}`}
            valueColor={theme.palette.primary.main}
          />
        )}
        {bill.walletUsed > 0 && (
          <BillRow label="Wallet Deduction" value={`-${money(bill.walletUsed)}`} valueColor={theme.palette.primary.main} />
        )}
        {/* Shown apart from the wallet, since the two pots are deducted and
            refunded separately -- with where the credit landed */}
        {bill.referralUsed > 0 && (
          <>
            <BillRow
              label="Referral Credit"
              value={`-${money(bill.referralUsed)}`}
              valueColor={theme.palette.primary.main}
            />
            {[
              ["Service fee", bill.referralSplit.onService],
              ["Delivery fee", bill.referralSplit.onDelivery],
              ["Order amount", bill.referralSplit.onOrder],
            ]
              .filter(([, amount]) => amount > 0)
              .map(([label, amount]) => (
                <BillRow key={label} muted label={label} value={`-${money(amount)}`} />
              ))}
          </>
        )}
        {restaurantData?.messageoftheDay && (
          <Box
            mt={1.5}
            p={1.5}
            style={{ background: "#F28C28", borderRadius: 10 }}
          >
            <Typography style={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem" }}>
              {restaurantData.messageoftheDay}
            </Typography>
          </Box>
        )}
        <Divider style={{ margin: "12px 0 6px" }} />
        <BillRow label={t("total")} value={money(bill.total)} bold />
      </Section>

      {!isPickUp && farDistanceNotice && (
        <Box
          mt={1.5}
          p={1.5}
          display="flex"
          alignItems="flex-start"
          style={{
            backgroundColor: "rgba(244, 118, 39, 0.10)",
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRadius: 8,
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: 16, color: "#6B6B6B", marginRight: 8, marginTop: 2 }} />
          <Box>
            <Typography style={{ fontSize: "0.8rem", color: "#555", lineHeight: 1.4 }}>
              {farDistanceNotice}
            </Typography>
            <Typography style={{ fontSize: "0.8rem", color: "#555", fontWeight: 700, marginTop: 3 }}>
              {t("termsAndConditionsApply", { defaultValue: "Terms and Conditions apply." })}
            </Typography>
          </Box>
        </Box>
      )}

      <Typography style={{ fontSize: "0.78rem", color: "#555", marginTop: 16 }}>
        By placing the order I agree to the{" "}
        <RouterLink to="/terms" style={{ color: theme.palette.primary.main, fontWeight: 700 }}>
          Terms and Conditions
        </RouterLink>{" "}
        and{" "}
        <RouterLink to="/privacy" style={{ color: theme.palette.primary.main, fontWeight: 700 }}>
          Privacy Policy
        </RouterLink>
        .
      </Typography>

      <Voucher
        isVisible={voucherModal}
        toggleModal={toggleVoucherModal}
        couponText={couponText}
        setCouponText={setCouponText}
        couponLoading={couponLoading}
        mutateVoucher={mutateVoucher}
      />

      <CreditDialog
        open={walletModal}
        onClose={() => setWalletModal(false)}
        icon={<AccountBalanceWalletOutlinedIcon color="primary" />}
        title={t("applyWalletBalance", { defaultValue: "Apply Wallet Balance" })}
        balanceLabel="Wallet balance"
        balance={wallet.walletBalance}
        hint="Loyalty credit can only be used against the service fee."
        max={wallet.maxWallet}
        applied={bill.walletUsed}
        onApply={wallet.applyWalletAmount}
        onClear={wallet.clearWallet}
      />

      <CreditDialog
        open={referralModal}
        onClose={() => {
          setReferralModal(false);
          setReferralPointsError("");
        }}
        icon={<CardGiftcardIcon color="primary" />}
        title="Apply Referral Credit"
        balanceLabel="Referral credit"
        balance={wallet.referralBalance}
        hint="Referral credit can pay for any part of this order."
        max={wallet.maxReferral}
        applied={bill.referralUsed}
        onApply={wallet.applyReferralAmount}
        onClear={wallet.clearReferral}
        extra={
          wallet.referralPoints > 0 && (
            <Box mt={2} p={2} style={{ border: "1px dashed #E0E0E0", borderRadius: 12 }}>
              <Typography style={{ fontSize: "0.85rem", color: "#111" }}>
                You have <b>{wallet.referralPoints}</b> referral points. Turn them into credit and
                apply them to this order.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                disabled={wallet.redeemingReferral}
                onClick={async () => {
                  const message = await wallet.useReferralPoints();
                  if (message) setReferralPointsError(message);
                  else setReferralModal(false);
                }}
                style={{ marginTop: 10, borderRadius: 10, fontWeight: 700 }}
              >
                {wallet.redeemingReferral ? <CircularProgress size={18} /> : "Use referral points"}
              </Button>
              {referralPointsError && (
                <Typography style={{ fontSize: "0.78rem", color: "#DC2626", marginTop: 6 }}>
                  {referralPointsError}
                </Typography>
              )}
            </Box>
          )
        }
      />

      <Dialog
        open={tipModal}
        onClose={() => setTipModal(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ style: { borderRadius: 20 } }}
      >
        <Box p={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography style={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>
              Custom tip
            </Typography>
            <IconButton size="small" onClick={() => setTipModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            value={tipInput}
            onChange={(e) => {
              setTipInput(e.target.value);
              setTipError("");
            }}
            placeholder="Enter amount"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0, step: "0.01", style: { color: "#111" } }}
            error={Boolean(tipError)}
            helperText={tipError || " "}
          />
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={applyCustomTip}
            style={{ borderRadius: 10, height: 44, fontWeight: 700 }}
          >
            {t("apply", { defaultValue: "Apply" })}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

export default React.memo(CartItemsCard);
